
import React, { useEffect, useRef } from 'react';
import { ArrowLeft, User } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import ConversationList from './ConversationList';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import { useChat } from '@/hooks/useChat';
import { MessageType } from '@/types/chat';
import { useMediaQuery } from '@/hooks/use-media-query';

interface ChatViewProps {
  onNavigateBack?: () => void;
  onViewProfile?: (participantId: number | string) => void;
  onNavigateToPatients?: () => void;
  userType: 'doctor' | 'patient';
  conversationId?: number | string;
}

const ChatView: React.FC<ChatViewProps> = ({
  onNavigateBack,
  onViewProfile,
  onNavigateToPatients,
  userType,
  conversationId
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  const {
    conversations,
    selectedConversationId,
    selectedConversation,
    selectedMessages,
    selectConversation,
    sendMessage
  } = useChat();
  
  // Select conversation based on route param
  useEffect(() => {
    if (conversationId) {
      selectConversation(conversationId);
    }
  }, [conversationId, selectConversation]);
  
  // Auto-scroll to bottom of chat when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedMessages]);
  
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };
  
  const handleSendMessage = (text: string, type: MessageType, file?: File) => {
    sendMessage(text, type, file);
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full min-h-[calc(100vh-200px)]">
      {/* Conversation List - Hidden on mobile when a conversation is selected */}
      {(!isMobile || !selectedConversationId) && (
        <div className="md:block">
          <ConversationList 
            conversations={conversations}
            selectedConversationId={selectedConversationId}
            onSelectConversation={(id) => selectConversation(id)}
            emptyStateMessage={`Start a conversation with ${userType === 'doctor' ? 'your patient' : 'your doctor'}.`}
          />
        </div>
      )}
      
      {/* Chat Window */}
      <div className={`${!isMobile ? 'md:col-span-2' : 'col-span-1'}`}>
        {selectedConversation ? (
          <Card className="flex flex-col h-[calc(100vh-200px)]">
            {/* Chat Header */}
            <CardHeader className="p-4 flex flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-3">
                {isMobile && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={onNavigateBack}
                    className="mr-1"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                )}
                <Avatar className="h-10 w-10">
                  {selectedConversation.participantAvatar ? (
                    <AvatarImage 
                      src={selectedConversation.participantAvatar} 
                      alt={selectedConversation.participantName} 
                    />
                  ) : (
                    <AvatarFallback>{getInitials(selectedConversation.participantName)}</AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h3 className="font-medium">{selectedConversation.participantName}</h3>
                  {selectedConversation.condition && (
                    <p className="text-xs text-muted-foreground">{selectedConversation.condition}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {onViewProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewProfile(selectedConversation.participantId)}
                  >
                    <User className="h-4 w-4 mr-1" />
                    View Profile
                  </Button>
                )}
              </div>
            </CardHeader>
            
            {/* Chat Messages */}
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-2">
              {selectedMessages.length > 0 ? (
                selectedMessages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isUserMessage={message.senderId === userType}
                  />
                ))
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-muted-foreground text-center">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </CardContent>
            
            {/* Message Input */}
            <CardFooter className="p-4 pt-2 border-t">
              <MessageInput onSendMessage={handleSendMessage} />
            </CardFooter>
          </Card>
        ) : (
          <Card className="h-[calc(100vh-200px)] flex items-center justify-center">
            <div className="text-center p-6">
              <h3 className="font-medium text-lg mb-2">No conversation selected</h3>
              <p className="text-muted-foreground mb-4">
                Select a conversation from the list or start a new one.
              </p>
              {onNavigateToPatients && (
                <Button 
                  variant="outline"
                  onClick={onNavigateToPatients}
                >
                  View Patients
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ChatView;
